import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { ArrowRight, Ticket, Coffee } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/transfers")({
  head: () => ({ meta: [{ title: "Transferler — JUËN Admin" }] }),
  component: AdminTransfersPage,
});

type Kind = "all" | "ticket" | "voucher";

interface TransferRow {
  id: string;
  kind: "ticket" | "voucher";
  item_id: string;
  from_user_id: string;
  to_user_id: string;
  event_id: string | null;
  code: string | null;
  created_at: string;
}

interface Profile { id: string; first_name: string; last_name: string; email: string }
interface EventRow { id: string; name: string; starts_at: string }
interface DrinkVoucherRow { id: string; drink_id: string }
interface DrinkRow { id: string; name: string }
interface TicketRow { id: string; ticket_type_id: string | null }
interface TicketTypeRow { id: string; name: string }

function AdminTransfersPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TransferRow[]>([]);
  const [profiles, setProfiles] = useState<Map<string, Profile>>(new Map());
  const [events, setEvents] = useState<Map<string, EventRow>>(new Map());
  const [drinkByVoucher, setDrinkByVoucher] = useState<Map<string, string>>(new Map());
  const [ttByTicket, setTtByTicket] = useState<Map<string, string>>(new Map());
  const [kind, setKind] = useState<Kind>("all");
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoadingList(true);
    let q = supabase.from("transfers").select("*").order("created_at", { ascending: false }).limit(500);
    if (kind !== "all") q = q.eq("kind", kind);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setLoadingList(false);
      return;
    }
    const list = (data ?? []) as TransferRow[];
    setRows(list);

    const userIds = [...new Set(list.flatMap((r) => [r.from_user_id, r.to_user_id]))];
    const eventIds = [...new Set(list.map((r) => r.event_id).filter(Boolean) as string[])];
    const voucherIds = list.filter((r) => r.kind === "voucher").map((r) => r.item_id);
    const ticketIds = list.filter((r) => r.kind === "ticket").map((r) => r.item_id);

    const [{ data: profs }, { data: evs }, { data: vouchers }, { data: tickets }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id,first_name,last_name,email").in("id", userIds)
        : Promise.resolve({ data: [] as Profile[] }),
      eventIds.length
        ? supabase.from("events").select("id,name,starts_at").in("id", eventIds)
        : Promise.resolve({ data: [] as EventRow[] }),
      voucherIds.length
        ? supabase.from("drink_vouchers").select("id,drink_id").in("id", voucherIds)
        : Promise.resolve({ data: [] as DrinkVoucherRow[] }),
      ticketIds.length
        ? supabase.from("tickets").select("id,ticket_type_id").in("id", ticketIds)
        : Promise.resolve({ data: [] as TicketRow[] }),
    ]);

    const drinkIds = [...new Set((vouchers ?? []).map((v) => v.drink_id))];
    const ttIds = [...new Set((tickets ?? []).map((t) => t.ticket_type_id).filter(Boolean) as string[])];

    const [{ data: drinks }, { data: tts }] = await Promise.all([
      drinkIds.length
        ? supabase.from("drinks").select("id,name").in("id", drinkIds)
        : Promise.resolve({ data: [] as DrinkRow[] }),
      ttIds.length
        ? supabase.from("ticket_types").select("id,name").in("id", ttIds)
        : Promise.resolve({ data: [] as TicketTypeRow[] }),
    ]);

    setProfiles(new Map((profs ?? []).map((p) => [p.id, p])));
    setEvents(new Map((evs ?? []).map((e) => [e.id, e])));
    const drinkMap = new Map((drinks ?? []).map((d) => [d.id, d.name]));
    setDrinkByVoucher(new Map((vouchers ?? []).map((v) => [v.id, drinkMap.get(v.drink_id) ?? "İçecek"])));
    const ttMap = new Map((tts ?? []).map((t) => [t.id, t.name]));
    setTtByTicket(new Map((tickets ?? []).map((t) => [t.id, t.ticket_type_id ? ttMap.get(t.ticket_type_id) ?? "Bilet" : "Bilet"])));
    setLoadingList(false);
  }, [kind]);

  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const tabs: { key: Kind; label: string }[] = [
    { key: "all", label: "Tümü" },
    { key: "ticket", label: "Biletler" },
    { key: "voucher", label: "İçecekler" },
  ];

  const fmtName = (id: string) => {
    const p = profiles.get(id);
    return p ? `${p.first_name} ${p.last_name}` : id.slice(0, 8);
  };
  const fmtEmail = (id: string) => profiles.get(id)?.email ?? "";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Transferler</h1>
          <p className="mt-2 text-muted-foreground">
            Üyeler arasındaki bilet ve içecek kuponu transferleri.
          </p>
        </div>

        <AdminTabs />

        <div className="mb-6 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          {tabs.map((t) => {
            const active = kind === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setKind(t.key)}
                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Henüz transfer yok.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const ev = r.event_id ? events.get(r.event_id) : null;
              const itemLabel =
                r.kind === "ticket"
                  ? ttByTicket.get(r.item_id) ?? "Bilet"
                  : drinkByVoucher.get(r.item_id) ?? "İçecek";
              return (
                <div key={r.id} className="rounded-2xl border border-border bg-card p-4 shadow-card">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                      r.kind === "ticket"
                        ? "bg-primary/10 text-primary"
                        : "bg-emerald-500/10 text-emerald-600"
                    }`}>
                      {r.kind === "ticket" ? <Ticket className="h-3 w-3" /> : <Coffee className="h-3 w-3" />}
                      {r.kind === "ticket" ? "Bilet" : "İçecek"}
                    </span>
                    <span>{new Date(r.created_at).toLocaleString("tr-TR")}</span>
                    {r.code && <span className="font-mono">#{r.code}</span>}
                  </div>
                  <div className="mt-2 text-sm font-semibold">{itemLabel}{ev ? ` · ${ev.name}` : ""}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <Link
                      to="/harikaradmin/members/$userId"
                      params={{ userId: r.from_user_id }}
                      className="text-foreground hover:underline"
                    >
                      {fmtName(r.from_user_id)}
                    </Link>
                    <span className="text-xs text-muted-foreground">{fmtEmail(r.from_user_id)}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Link
                      to="/harikaradmin/members/$userId"
                      params={{ userId: r.to_user_id }}
                      className="text-foreground hover:underline"
                    >
                      {fmtName(r.to_user_id)}
                    </Link>
                    <span className="text-xs text-muted-foreground">{fmtEmail(r.to_user_id)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
